/**
 * AcceptOfferModal — confirm acceptance of an offer.
 *
 * Replaces the inline modal previously embedded in OfferManagement.js
 * (lines ~1663–1694). Uses ui/Modal for the shell.
 */
import React, { useId } from 'react';
import Modal from '../../ui/Modal';
import { formatPrice } from '../utils';
import {
  financingLoanAmount,
  getContingencyLabelWithDeadline,
  getCureElectionSummary,
  getFinancingTermsSummary,
  getLeadPaintElectionSummary,
  getSummaryContingencyKeys,
  getTitlePeriodsSummary,
} from '../contingencyCatalog';
import { formatDeadline } from '../offerExpiration';
import './AcceptOfferModal.css';

const renderContingencies = (contingencies) => {
  const kept = getSummaryContingencyKeys(contingencies).map((key) =>
    getContingencyLabelWithDeadline(key, contingencies)
  );
  return kept.length ? kept.join(', ') : 'None';
};

export default function AcceptOfferModal({
  open,
  onClose,
  offer,
  currentRole, // eslint-disable-line no-unused-vars
  // Set to the state code when cutthe6.com can't prepare that state's
  // purchase agreement, so the warning doesn't promise one.
  documentsUnsupportedState = null,
  submitting = false,
  onSubmit,
  error,
}) {
  const promptId = useId();
  const errorId = useId();

  const propertyLabel =
    offer?.property_address || (offer?.property_id ? `property #${offer.property_id}` : 'this property');
  // The WI loan terms and title periods being accepted (QA N13), when recorded.
  const financingSummary = offer
    ? getFinancingTermsSummary(
      offer.contingencies, financingLoanAmount(offer.offer_price, offer.down_payment_percent),
    )
    : null;
  const titleSummary = offer ? getTitlePeriodsSummary(offer.contingencies) : null;

  const handleAccept = () => {
    if (submitting || !offer) return;
    onSubmit?.(offer);
  };

  const footer = (
    <>
      {error && (
        <p id={errorId} role="alert" className="accept-modal__error">
          {error}
        </p>
      )}
      <div className="accept-modal__actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-success"
          onClick={handleAccept}
          disabled={submitting || !offer}
          aria-describedby={error ? errorId : undefined}
        >
          {submitting ? 'Accepting…' : 'Accept offer'}
        </button>
      </div>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Accept this offer?"
      size="sm"
      describedBy={promptId}
      footer={footer}
    >
      <p id={promptId} className="accept-modal__prompt">
        Ready to move forward?
        <strong>{propertyLabel}</strong>
      </p>

      {offer && (
        <dl className="accept-modal__summary" aria-label="Offer summary">
          <div className="accept-modal__row">
            <dt>Price</dt>
            <dd className="accept-modal__price">{formatPrice(offer.offer_price)}</dd>
          </div>
          <div className="accept-modal__row">
            <dt>Down payment</dt>
            <dd>{offer.down_payment_percent}%</dd>
          </div>
          {offer.earnest_money != null && (
            <div className="accept-modal__row">
              <dt>Earnest money</dt>
              <dd data-testid="accept-earnest">{formatPrice(offer.earnest_money)}</dd>
            </div>
          )}
          <div className="accept-modal__row">
            <dt>Closing date</dt>
            <dd>{offer.closing_date || '—'}</dd>
          </div>
          {offer.expires_at && (
            <div className="accept-modal__row">
              <dt>Offer expires</dt>
              <dd>{formatDeadline(offer.expires_at, offer.property_timezone)}</dd>
            </div>
          )}
          <div className="accept-modal__row">
            <dt>Contingencies</dt>
            <dd>{renderContingencies(offer.contingencies)}</dd>
          </div>
          {getLeadPaintElectionSummary(offer.contingencies) && (
            <div className="accept-modal__row">
              <dt>Lead paint</dt>
              <dd data-testid="accept-lead-paint">{getLeadPaintElectionSummary(offer.contingencies)}</dd>
            </div>
          )}
          {getCureElectionSummary(offer.contingencies) && (
            <div className="accept-modal__row">
              <dt>Cure right</dt>
              <dd data-testid="accept-cure">{getCureElectionSummary(offer.contingencies)}</dd>
            </div>
          )}
          {financingSummary && (
            <div className="accept-modal__row">
              <dt>Loan terms</dt>
              <dd data-testid="accept-financing-terms">{financingSummary}</dd>
            </div>
          )}
          {titleSummary && (
            <div className="accept-modal__row">
              <dt>Title</dt>
              <dd data-testid="accept-title-periods">{titleSummary}</dd>
            </div>
          )}
          {offer.additional_terms && (
            <div className="accept-modal__row">
              <dt>Additional terms</dt>
              <dd data-testid="accept-additional-terms">{offer.additional_terms}</dd>
            </div>
          )}
        </dl>
      )}

      {documentsUnsupportedState ? (
        <div className="accept-modal__warning" role="note" data-testid="accept-docs-unsupported">
          {/* Legal review 2026-10-24: no "no contract exists" conclusion here — where the
              platform runs no signing workflow it can't vouch for when a contract forms. */}
          <strong>Important:</strong> Accepting locks in these terms on cutthe6.com, but it
          isn&apos;t a signed purchase agreement. cutthe6.com can&apos;t prepare one
          in {documentsUnsupportedState} yet, so you and the other party will need to prepare and
          sign it outside cutthe6.com, for example with a real estate attorney. You can&apos;t undo
          an acceptance in cutthe6.com. General information only, not legal advice.
        </div>
      ) : (
        <div className="accept-modal__warning" role="note">
          <strong>Important:</strong> Accepting locks in these terms on cutthe6.com and sends
          the purchase agreement to both of you to sign. The agreement becomes binding once
          everyone required has signed it. You can&apos;t undo an acceptance in cutthe6.com.
        </div>
      )}
    </Modal>
  );
}
